'use client';

import { c as _c } from "react/compiler-runtime";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useModal, useTurnkey, TurnkeyProvider as WalletKitProvider } from '@turnkey/react-wallet-kit';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAuthSync } from '../../../../auth/oauth/auth-sync.js';
import { isPopupOauthCallbackHref } from '../../../../auth/oauth/is-popup-oauth-callback.js';
import { decodeJwtPayload, detectOAuthProvider } from '../../../../auth/oauth/oauth-claims.js';
import { readOauthReturnError } from '../../../../auth/oauth/oauth-return-error.js';
import { TurnkeyModalActiveProvider } from '../../../../auth/turnkey/turnkey-modal-context.js';
import { runResolvedLogin } from '../../../shared/auth/session/resolve-login.js';
import { OauthConnectStash } from '../oauth/oauth-connect-stash.js';
import { TurnkeySessionSteward } from './turnkey-session-steward.js';
/**
 * Detect browsers likely to block `window.open` for the Turnkey OAuth
 * popup (Safari + mobile lose the user-gesture token after the async PKCE
 * setup). Those default to full-page redirect mode.
 */
function shouldDefaultToOauthRedirect() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isSafari = /^((?!chrome|crios|fxios|android).)*safari/i.test(ua);
  const isMobile = /iPhone|iPad|iPod|Android|Mobile/i.test(ua);
  return isSafari || isMobile;
}
/** Flatten an `Error.cause` (which can be anything) into an event-safe string. */
function describeCause(cause) {
  if (cause instanceof Error) return `${cause.name}: ${cause.message}`;
  if (typeof cause === 'string') return cause;
  try {
    return JSON.stringify(cause);
  } catch {
    return String(cause);
  }
}
/**
 * SDK-owned web Turnkey provider. Wraps `@turnkey/react-wallet-kit`'s
 * `TurnkeyProvider`, wires the Turnkey callbacks into the SDK auth-sync
 * bridge, and exposes the modal-active state.
 */
export function SdkTurnkeyWebProvider(t0) {
  const $ = _c(61);
  const {
    config,
    isDark,
    onAuthEvent,
    children
  } = t0;
  const {
    setFreshLogin,
    consumeOAuthProfile,
    runCompleteOauth,
    setConnectFailure,
    setPendingConnect,
    signalSessionExpired,
    signalOauthLoginStarted,
    signalOauthLoginFailed
  } = useAuthSync();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = typeof window !== "undefined" && isPopupOauthCallbackHref(window.location.href);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [skipPopupCallback, setSkipPopupCallback] = useState(t1);
  const onAuthEventRef = useRef(onAuthEvent);
  let t2;
  let t3;
  if ($[1] !== onAuthEvent) {
    t2 = () => {
      onAuthEventRef.current = onAuthEvent;
    };
    t3 = [onAuthEvent];
    $[1] = onAuthEvent;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useLayoutEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (!isPopupOauthCallbackHref(window.location.href)) {
        return;
      }
      setSkipPopupCallback(true);
      onAuthEventRef.current?.("turnkey_popup_callback_skip", {
        had_code: new URLSearchParams(window.location.search).has("code"),
        had_id_token: window.location.hash.includes("id_token=")
      });
    };
    t5 = [];
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useLayoutEffect(t4, t5);
  const t6 = config.organizationId;
  const t7 = config.apiBaseUrl;
  const t8 = config.authProxyConfigId;
  const t9 = config.oauthRedirectUri;
  let t10;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = shouldDefaultToOauthRedirect();
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  let t11;
  if ($[7] !== config.xClientId) {
    t11 = {
      primaryClientId: config.xClientId
    };
    $[7] = config.xClientId;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  let t12;
  if ($[9] !== config.oauthRedirectUri || $[10] !== t11) {
    t12 = {
      oauthRedirectUri: t9,
      openOauthInPage: t10,
      x: t11
    };
    $[9] = config.oauthRedirectUri;
    $[10] = t11;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = _temp();
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] !== t12) {
    t14 = {
      autoRefreshSession: true,
      oauthConfig: t12,
      createSuborgParams: t13
    };
    $[13] = t12;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      features: {
        auth: false,
        connecting: true
      },
      chains: {
        solana: {
          native: true
        },
        ethereum: {
          native: false
        }
      }
    };
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  let t16;
  if ($[16] !== config.lightPrimaryHex) {
    t16 = {
      primary: config.lightPrimaryHex
    };
    $[16] = config.lightPrimaryHex;
    $[17] = t16;
  } else {
    t16 = $[17];
  }
  let t17;
  if ($[18] !== config.darkPrimaryHex) {
    t17 = {
      primary: config.darkPrimaryHex
    };
    $[18] = config.darkPrimaryHex;
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  if ($[20] !== t16 || $[21] !== t17) {
    t18 = {
      light: t16,
      dark: t17
    };
    $[20] = t16;
    $[21] = t17;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] !== config.borderRadius || $[24] !== config.logoDark || $[25] !== config.logoLight || $[26] !== isDark || $[27] !== t18) {
    t19 = {
      darkMode: isDark,
      logoLight: config.logoLight,
      logoDark: config.logoDark,
      colors: t18,
      preferLargeActionButtons: true,
      borderRadius: config.borderRadius,
      backgroundBlur: "10px",
      supressMissingStylesError: true
    };
    $[23] = config.borderRadius;
    $[24] = config.logoDark;
    $[25] = config.logoLight;
    $[26] = isDark;
    $[27] = t18;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== config.apiBaseUrl || $[30] !== config.authProxyConfigId || $[31] !== config.organizationId || $[32] !== t14 || $[33] !== t19) {
    t20 = {
      organizationId: t6,
      apiBaseUrl: t7,
      authProxyConfigId: t8,
      autoFetchWalletKitConfig: true,
      auth: t14,
      walletConfig: t15,
      ui: t19
    };
    $[29] = config.apiBaseUrl;
    $[30] = config.authProxyConfigId;
    $[31] = config.organizationId;
    $[32] = t14;
    $[33] = t19;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  const turnkeyConfig = t20;
  let t21;
  if ($[35] !== onAuthEvent || $[36] !== runCompleteOauth || $[37] !== setPendingConnect || $[38] !== signalOauthLoginFailed || $[39] !== signalOauthLoginStarted) {
    t21 = t22 => {
      const {
        idToken,
        publicKey
      } = t22;
      const detected = detectOAuthProvider(idToken);
      const providerName = detected.provider;
      if (providerName === null) {
        console.warn("[turnkey] onOauthRedirect \u2014 could not detect provider, dropping login");
        return;
      }
      if (providerName === "x") {
        const stash = OauthConnectStash.read();
        if (stash !== null && stash.provider === "x") {
          OauthConnectStash.clear();
          setPendingConnect({
            oidcToken: idToken,
            providerName: "x",
            returnTo: typeof stash.returnTo === "string" && stash.returnTo.startsWith("/") ? stash.returnTo : "/",
            oauthPublicKey: publicKey
          });
          return;
        }
      }
      signalOauthLoginStarted();
      runResolvedLogin({
        oidcToken: idToken,
        publicKey,
        providerName,
        runCompleteOauth,
        signalOauthLoginFailed
      }).catch(err => {
        console.error("[turnkey] resolved login failed", providerName, err);
        onAuthEvent?.("auth_error", {
          error: err instanceof Error ? err.message : String(err),
          method: "oauth",
          context: "complete_oauth_login",
          provider: providerName,
          ...(err instanceof Error && typeof err.cause !== "undefined" ? {
            cause: describeCause(err.cause)
          } : {})
        });
        signalOauthLoginFailed();
      });
    };
    $[35] = onAuthEvent;
    $[36] = runCompleteOauth;
    $[37] = setPendingConnect;
    $[38] = signalOauthLoginFailed;
    $[39] = signalOauthLoginStarted;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== consumeOAuthProfile || $[42] !== setFreshLogin) {
    t22 = t23 => {
      const {
        session,
        method,
        identifier
      } = t23;
      const token = session?.token;
      if (token === undefined || token === null || token === "") {
        return;
      }
      let oauthProfile = consumeOAuthProfile();
      if (oauthProfile === null && method === "oauth" && identifier.includes(".")) {
        const claims = decodeJwtPayload(identifier);
        const detected_0 = detectOAuthProvider(identifier);
        oauthProfile = {
          name: typeof claims.name === "string" ? claims.name : null,
          picture: typeof claims.picture === "string" ? claims.picture : null,
          email: typeof claims.email === "string" ? claims.email : null,
          provider: detected_0.provider,
          providerUserId: typeof claims.sub === "string" ? claims.sub : null,
          xUserId: detected_0.xUserId,
          oidcToken: identifier
        };
      }
      setFreshLogin({
        sessionToken: token,
        turnkeyMethod: method,
        oauthProfile
      });
    };
    $[41] = consumeOAuthProfile;
    $[42] = setFreshLogin;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== onAuthEvent || $[45] !== signalSessionExpired) {
    t23 = () => {
      onAuthEvent?.("turnkey_session_expired", {});
      signalSessionExpired();
    };
    $[44] = onAuthEvent;
    $[45] = signalSessionExpired;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== onAuthEvent || $[48] !== setConnectFailure) {
    t24 = error => {
      const msg = error.message.toLowerCase();
      const isUserCancellation = msg.includes("cancelled") || msg.includes("canceled") || msg.includes("closed") || msg.includes("rejected") || msg.includes("user denied");
      const isConnectRejection = msg.includes("already been imported") || msg.includes("already exists");
      const isSessionAbsenceNoise = msg.includes("no active session found to log out from");
      if (isUserCancellation) {
        return;
      }
      if (isConnectRejection) {
        console.warn("[turnkey] connect rejection", error.message);
        setConnectFailure("already-linked-elsewhere");
        return;
      }
      if (isSessionAbsenceNoise) {
        console.warn("[turnkey] session-absent noise", error.message);
        return;
      }
      console.error("[turnkey] auth error", error.message, error);
      onAuthEvent?.("auth_error", {
        error: error.message,
        method: null,
        context: "turnkey_on_error",
        name: error.name,
        ...(typeof error.cause !== "undefined" ? {
          cause: describeCause(error.cause)
        } : {})
      });
      setConnectFailure("turnkey-error");
    };
    $[47] = onAuthEvent;
    $[48] = setConnectFailure;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t21 || $[51] !== t22 || $[52] !== t23 || $[53] !== t24) {
    t25 = {
      beforeSessionExpiry: _temp2,
      onOauthRedirect: t21,
      onAuthenticationSuccess: t22,
      onSessionExpired: t23,
      onError: t24
    };
    $[50] = t21;
    $[51] = t22;
    $[52] = t23;
    $[53] = t24;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  const turnkeyCallbacks = t25;
  if (skipPopupCallback) {
    let t26;
    if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = _jsx(PopupOauthCallbackShell, {});
      $[55] = t26;
    } else {
      t26 = $[55];
    }
    return t26;
  }
  let t26;
  if ($[56] !== children || $[57] !== onAuthEvent || $[58] !== turnkeyCallbacks || $[59] !== turnkeyConfig) {
    t26 = _jsx(WalletKitProvider, {
      config: turnkeyConfig,
      callbacks: turnkeyCallbacks,
      children: _jsx(TurnkeyModalBridge, {
        onAuthEvent,
        children
      })
    });
    $[56] = children;
    $[57] = onAuthEvent;
    $[58] = turnkeyCallbacks;
    $[59] = turnkeyConfig;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  return t26;
}
/** Matches `/auth/callback` hang-redirect: if the opener is gone, close us. */
function _temp2() {}
function _temp() {
  const wallet = {
    walletName: "Default Wallet",
    walletAccounts: [{
      curve: "CURVE_ED25519",
      pathFormat: "PATH_FORMAT_BIP32",
      path: "m/44'/501'/0'/0'",
      addressFormat: "ADDRESS_FORMAT_SOLANA"
    }]
  };
  return {
    emailOtpAuth: {
      subOrgName: "Morfi (email)",
      customWallet: wallet
    },
    oauth: {
      subOrgName: "Morfi (oauth)",
      customWallet: wallet
    }
  };
}
const POPUP_ORPHAN_CLOSE_MS = 25_000;
/**
 * True when the provider sent the popup back with an OAuth error and no
 * credential (the user cancelled on the consent page). The opener's kit poller
 * only reacts to a code or id_token, so it learns about the cancel from this
 * window closing.
 */
function isPopupOauthErrorReturn() {
  return readOauthReturnError(window.location.search, window.location.hash) !== null;
}
/** Popup document placeholder — opener polls the URL and closes the window. */
function PopupOauthCallbackShell() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp4, t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = _jsx("div", {
      role: "status",
      "aria-live": "polite"
    });
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
/**
 * Reads the Turnkey modal stack via `useModal()` (only callable inside
 * `WalletKitProvider`) and exposes it through `TurnkeyModalActiveProvider`
 * so app dialogs can suppress dismissal while a Turnkey modal is open.
 * Also registers `logout` + `completeOauth` on the auth-sync bridge, and
 * mounts the session steward (rolling refresh + dead-session reconcile).
 */
function _temp4() {
  const delay = isPopupOauthErrorReturn() ? 0 : POPUP_ORPHAN_CLOSE_MS;
  const id = window.setTimeout(_temp3, delay);
  return () => window.clearTimeout(id);
}
function _temp3() {
  window.close();
}
function TurnkeyModalBridge(t0) {
  const $ = _c(12);
  const {
    children,
    onAuthEvent
  } = t0;
  const {
    modalStack
  } = useModal();
  const {
    logout: tkLogout,
    completeOauth
  } = useTurnkey();
  const {
    registerTurnkeyLogout,
    registerCompleteOauth
  } = useAuthSync();
  let t1;
  let t2;
  if ($[0] !== registerTurnkeyLogout || $[1] !== tkLogout) {
    t1 = () => {
      registerTurnkeyLogout(async () => {
        await tkLogout();
      });
    };
    t2 = [tkLogout, registerTurnkeyLogout];
    $[0] = registerTurnkeyLogout;
    $[1] = tkLogout;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== completeOauth || $[5] !== registerCompleteOauth) {
    t3 = () => {
      registerCompleteOauth(async params => {
        await completeOauth({
          oidcToken: params.oidcToken,
          publicKey: params.publicKey,
          providerName: params.providerName,
          ...(params.sessionKey !== undefined ? {
            sessionKey: params.sessionKey
          } : {})
        });
      });
    };
    t4 = [completeOauth, registerCompleteOauth];
    $[4] = completeOauth;
    $[5] = registerCompleteOauth;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  const t5 = modalStack.length > 0;
  let t6;
  if ($[8] !== children || $[9] !== onAuthEvent || $[10] !== t5) {
    t6 = _jsxs(TurnkeyModalActiveProvider, {
      active: t5,
      children: [_jsx(TurnkeySessionSteward, {
        onAuthEvent
      }), children]
    });
    $[8] = children;
    $[9] = onAuthEvent;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}